import { useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { adminApi } from '@/api/adminApi'
import type { AdminRiskRow } from '@/api/types'
import { AdminInput, AdminPageHeader, AdminSelect, Money } from '@/components/admin/AdminUi'
import { EmptyState } from '@/components/ui/EmptyState'
import { Skeleton } from '@/components/ui/Skeleton'
import { Button } from '@/components/ui/Button'
import { usePaginatedList } from '@/hooks/usePaginatedList'

type Filters = { search: string; signal: string; plan: string; status: string }

export default function RiskPage() {
  const { t } = useTranslation()
  const fetcher = useCallback(
    ({ limit, offset, ...filters }: { limit: number; offset: number } & Filters) =>
      adminApi.risk({ limit, offset, ...filters }),
    [],
  )
  const list = usePaginatedList<AdminRiskRow, Filters>(fetcher, {
    initialFilters: { search: '', signal: '', plan: '', status: '' },
  })
  return (
    <div>
      <AdminPageHeader
        title={t('admin.risk.title', 'Fila de risco')}
        description={t('admin.risk.description', 'Contas priorizadas por valor mensal × urgência, com sinais explicáveis.')}
      />
      <div className="mb-4 flex flex-wrap gap-2">
        <AdminInput aria-label={t('common.search', 'Buscar')} placeholder={t('admin.risk.search', 'Workspace, owner ou e-mail')} value={list.filters.search} onChange={(event) => list.setFilter('search', event.target.value)} />
        <AdminSelect value={list.filters.signal} onChange={(event) => list.setFilter('signal', event.target.value)}>
          <option value="">{t('admin.risk.allSignals', 'Todos os sinais')}</option>
          {['past_due', 'failed_charge', 'scheduled_cancellation', 'scheduled_downgrade', 'trial_ending', 'stuck_checkout', 'inactive'].map((value) => <option key={value} value={value}>{t(`admin.signals.${value}`, value)}</option>)}
        </AdminSelect>
        <AdminSelect value={list.filters.plan} onChange={(event) => list.setFilter('plan', event.target.value)}>
          <option value="">{t('admin.risk.allPlans', 'Todos os planos')}</option>
          <option value="free">Free</option><option value="pro">Pro</option><option value="team">Team</option>
        </AdminSelect>
      </div>
      {list.loading ? <div className="space-y-2"><Skeleton height={52} /><Skeleton height={52} /><Skeleton height={52} /></div> : null}
      {list.error ? <EmptyState title={t('common.errorLoading', 'Erro ao carregar')} description={list.error} actions={<Button onClick={list.reload}>{t('common.retry', 'Tentar novamente')}</Button>} /> : null}
      {!list.loading && !list.error && list.items.length === 0 ? <EmptyState title={t('admin.risk.empty', 'Nenhuma conta em risco')} description={t('admin.risk.emptyDescription', 'Os filtros atuais não encontraram sinais que exijam ação.')} /> : null}
      {!list.loading && list.items.length > 0 ? (
        <div className="overflow-x-auto rounded-xl border" style={{ borderColor: 'var(--border)' }}>
          <table className="w-full min-w-[900px] text-left text-xs">
            <thead style={{ background: 'var(--bg-toolbar)', color: 'var(--text-dim)' }}><tr><th className="p-3">Workspace</th><th className="p-3">Plano</th><th className="p-3">Valor</th><th className="p-3">Urgência</th><th className="p-3">Score</th><th className="p-3">Sinais</th><th className="p-3">Prazo</th></tr></thead>
            <tbody>{list.items.map((row) => (
              <tr key={row.workspace_id} className="border-t" style={{ borderColor: 'var(--border)', color: 'var(--text)' }}>
                <td className="p-3"><Link className="font-semibold" style={{ color: 'var(--accent)' }} to={`/admin/accounts/${row.workspace_id}`}>{row.workspace_name}</Link><div style={{ color: 'var(--text-dim)' }}>{row.owner_emails.join(', ')}</div></td>
                <td className="p-3 uppercase">{row.plan_code} · {row.status}</td>
                <td className="p-3"><Money cents={row.monthly_value_cents} />{!row.contract_complete ? <span className="ml-1" title="Contrato não reconciliado">⚠</span> : null}</td>
                <td className="p-3">{row.urgency}/5</td><td className="p-3 font-semibold">{row.risk_score}</td>
                <td className="p-3"><div className="flex flex-wrap gap-1">{row.signals.map((signal) => <span key={signal} className="rounded px-2 py-1" style={{ background: 'var(--color-warning-bg)', color: 'var(--color-warning)' }}>{t(`admin.signals.${signal}`, signal)}</span>)}</div></td>
                <td className="p-3">{row.deadline ? new Date(row.deadline).toLocaleString() : '—'}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      ) : null}
      {list.totalPages > 1 ? <div className="mt-4 flex items-center justify-end gap-2"><Button disabled={list.page <= 1} onClick={() => list.setPage(list.page - 1)}>‹</Button><span className="text-xs">{list.page}/{list.totalPages}</span><Button disabled={list.page >= list.totalPages} onClick={() => list.setPage(list.page + 1)}>›</Button></div> : null}
    </div>
  )
}
